// Hugeicons Pro icons, in the CivicLift-wide style chosen in Platform Settings. The layouts render
// that style's shapes for the icons listed under "frontend" in config/hugeicons.json into
// <script type="application/json" id="civiclift-hugeicons"> (IconStyleHelper#hugeicons_frontend_data_tag),
// so the JavaScript bundle carries no icon data. To add an icon, list it there and run bin/hugeicons-sync.

type IconNode = [string, Record<string, string>]
export type HugeiconKey = string

export const ICON_STYLES = [
  "stroke-rounded",
  "stroke-sharp",
  "stroke-standard",
  "solid-rounded",
  "solid-sharp",
  "solid-standard",
  "bulk-rounded",
  "duotone-rounded",
  "duotone-standard",
  "twotone-rounded",
] as const
export type IconStyle = (typeof ICON_STYLES)[number]

export const DEFAULT_ICON_STYLE: IconStyle = "stroke-rounded"

// The CivicLift-wide icon style, chosen in Platform Settings and rendered into a <meta> tag
// by the layouts.
export const currentIconStyle = (): IconStyle => {
  const value = document.querySelector<HTMLMetaElement>('meta[name="civiclift-icon-style"]')?.content
  return value && (ICON_STYLES as readonly string[]).includes(value) ? (value as IconStyle) : DEFAULT_ICON_STYLE
}

let cachedSource: string | null = null
let cachedIcons: Record<string, IconNode[]> = {}

// The shapes the page carries, keyed by icon. Re-read when the page's data changes.
const pageIcons = (): Record<string, IconNode[]> => {
  const source = document.getElementById("civiclift-hugeicons")?.textContent ?? ""
  if (source !== cachedSource) {
    cachedSource = source
    try {
      const icons = source ? JSON.parse(source).icons : null
      cachedIcons = icons && typeof icons === "object" ? icons : {}
    } catch {
      cachedIcons = {}
    }
  }
  return cachedIcons
}

const escapeAttr = (value: string): string =>
  value.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;")

// An icon's shapes as SVG markup for a 24x24 box, with currentColor resolved to `color`, for
// composing into a larger SVG. Null for an icon the page doesn't carry.
export const hugeiconShapes = (key: HugeiconKey, { color = "currentColor" }: { color?: string } = {}): string | null => {
  const icons = pageIcons()
  if (!Object.prototype.hasOwnProperty.call(icons, key) || !Array.isArray(icons[key])) return null
  return icons[key]
    .map(([tag, attrs]) => {
      const attributes = Object.entries(attrs)
        .map(([name, value]) => `${name}="${escapeAttr(value === "currentColor" ? color : value)}"`)
        .join(" ")
      return `<${tag} ${attributes}/>`
    })
    .join("")
}

// An icon as a standalone SVG string, with currentColor resolved to `color`
// (canvas-drawn SVGs have no CSS color to inherit). Null for an icon the page doesn't carry.
export const hugeiconSvg = (
  key: HugeiconKey,
  { color = "currentColor", size = 24 }: { color?: string; size?: number } = {}
): string | null => {
  const children = hugeiconShapes(key, { color })
  if (children === null) return null
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="${Number(size)}px" height="${Number(size)}px" fill="none">${children}</svg>`
}
